@file:OptIn(ExperimentalUuidApi::class)

package ai.wagemix.data

import com.fsryan.foundation.data.FSBaseSyncDataSource
import com.fsryan.foundation.data.FSSyncEvent
import com.fsryan.foundation.util.log.FSLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.SupervisorJob
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * The shape every Wagemix sync data source shares (sync data source playbook, Steps 3 and 4): an
 * in-flight guard in the local context per operation and scope, the foundation pipeline, and the
 * guard released on every terminal path. Subclasses supply the events and the remote call.
 */
abstract class WMBaseSyncDataSource<E: FSSyncEvent<E>> protected constructor(
    protected val localContext: WMLocalContextDataSource,
    scope: CoroutineScope = CoroutineScope(SupervisorJob())
): FSBaseSyncDataSource<E>(scope) {

    /**
     * The foundation pipeline catches `Exception`; on JavaScript a failed `fetch` surfaces as a
     * `Throwable` that is not one (Ktor's `JsError`), which would escape the pipeline and crash the
     * page. Everything that is not already an exception becomes a [WMRemoteException] here.
     */
    private suspend fun <T> asException(block: suspend () -> T): T {
        try {
            return block()
        } catch (e: Exception) {
            throw e
        } catch (t: Throwable) {
            throw WMRemoteException(status = 0, message = t.message ?: "The request could not be sent", cause = t)
        }
    }

    /**
     * Wins the guard for [operation] on [scope] or returns without emitting; otherwise emits
     * [initial], runs [remote] then [local], and emits [success] or [failure] with the guard removed.
     */
    protected suspend fun <R> guarded(
        operation: String,
        scope: String,
        sequenceId: Uuid,
        initial: () -> E,
        success: () -> E,
        failure: (Throwable) -> E,
        remote: suspend () -> R,
        local: suspend (R) -> Unit
    ) {
        if (!localContext.insertSyncIfAbsent(operation, scope, sequenceId.toString())) {
            return
        }
        performSync(
            createInitialSyncEvent = initial,
            fetchRemoteData = { asException { remote() } },
            updateLocalData = { fetched -> asException { local(fetched) } },
            onRemoteError = { error ->
                localContext.removeSync(operation, scope)
                FSLog.sendWMSyncFailure(operation, scope, sequenceId.toString(), error)
                failure(error)
            },
            onLocalError = { error ->
                localContext.removeSync(operation, scope)
                FSLog.sendWMSyncFailure(operation, scope, sequenceId.toString(), error)
                failure(error)
            },
            onSuccess = {
                localContext.removeSync(operation, scope)
                success()
            }
        )
    }
}
